import Button from "@/Components/Button/Button";
import ListTab, { ListTabItem } from "@/Components/List/ListTab";
import Modal from "@/Components/Modal/Modal";
import ModalTitle from "@/Components/Modal/ModalTitle";
import {
  AccessDeniedEntry,
  useAccessDenied,
} from "@/shared/atoms/useAccessDenied";
import { useCopyToClipboard } from "@/shared/hooks/useCopyToClipboard";
import useRoleHooks from "@/shared/hooks/useRoleHooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type Tab = "info" | "details";

// Plain text for the programmers, the user copies it into the report to Appsmen
const buildReport = (entries: AccessDeniedEntry[], roles: string[]) =>
  [
    `App: ${window.location.origin}`,
    `Page: ${entries[0].page}`,
    `Roles: ${roles.join(", ") || "-"}`,
    "",
    ...entries.map((entry, index) =>
      [
        entry.kind === "page"
          ? `${index + 1}. Page ${entry.page}`
          : `${index + 1}. Request ${entry.method} ${entry.url}`,
        entry.params ? `   Params: ${JSON.stringify(entry.params)}` : null,
        entry.status ? `   Status: ${entry.status}` : null,
        entry.errorId || entry.errorMessage
          ? `   Error: ${[entry.errorId, entry.errorMessage]
              .filter(Boolean)
              .join(" - ")}`
          : null,
        `   Time: ${entry.time}`,
      ]
        .filter((line) => line !== null)
        .join("\n")
    ),
  ].join("\n");

// Shown instead of signing the user out when their role has no access to a page or request
const AccessDeniedModal = () => {
  const { t } = useTranslation("", { keyPrefix: "accessDenied" });
  const { entries, dismiss } = useAccessDenied();
  const { getRoles } = useRoleHooks(false);
  const [tab, setTab] = useState<Tab>("info");
  const [copied, copy] = useCopyToClipboard();

  if (entries.length === 0) return null;

  const isPage = entries.some((entry) => entry.kind === "page");
  const report = buildReport(entries, getRoles.data?.roles ?? []);

  const handleClose = () => {
    setTab("info");
    dismiss();
  };

  return (
    <Modal onExit={handleClose} width={{ _: "95%", md: "70rem" }}>
      <ModalTitle>{t("title")}</ModalTitle>

      <ListTab>
        {(["info", "details"] as Tab[]).map((key) => (
          <ListTabItem
            key={key}
            active={tab === key}
            onClick={() => setTab(key)}
          >
            {t(`tabs.${key}`)}
          </ListTabItem>
        ))}
      </ListTab>

      {tab === "info" ? (
        <div className="mb-8 flex flex-col gap-4">
          <p>{isPage ? t("page") : t("request")}</p>
          <p className="text-text-secondary">{t("report")}</p>
        </div>
      ) : (
        <div className="mb-8 flex flex-col gap-4">
          <p className="text-text-secondary">{t("detailsHint")}</p>
          <pre className="max-h-[40vh] overflow-auto whitespace-pre-wrap break-all rounded-lg border border-border bg-grey-secondary p-4 text-small">
            {report}
          </pre>
        </div>
      )}

      <div className="flex gap-4">
        <Button onClick={handleClose}>{t("close")}</Button>
        {tab === "details" && (
          <Button variant="secondary" onClick={() => copy(report)}>
            {copied === report ? t("copied") : t("copy")}
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default AccessDeniedModal;
